import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, RotateCcw, RefreshCw } from 'lucide-react';
import { logger } from '../../lib/logger';

// ============================================================
// CHUNK LOAD ERROR DETECTION & LOOP PROTECTION
// ============================================================

const CHUNK_RELOAD_KEY = 'chunk_reload_at';
const CHUNK_RELOAD_COOLDOWN_MS = 15_000;

function isChunkLoadError(error: Error): boolean {
  const msg = error?.message ?? '';
  const name = error?.name ?? '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed')
  );
}

function shouldAutoReload(): boolean {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return true;
    const lastReloadAt = parseInt(raw, 10);
    if (isNaN(lastReloadAt)) return true;
    return Date.now() - lastReloadAt > CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
}

function recordChunkReload(): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
  } catch {
    // Ignore
  }
}

// ============================================================

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      if (shouldAutoReload()) {
        logger.warn('[ErrorBoundary] Chunk load error — auto-reloading', {
          error: error.message,
          timestamp: new Date().toISOString(),
        });
        // Record BEFORE reload so sessionStorage write is not lost
        recordChunkReload();
        window.location.reload();
        return;
      }
      logger.warn('[ErrorBoundary] Chunk load error — reload suppressed (loop guard)', {
        error: error.message,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    logger.error('[ErrorBoundary] Caught error', {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, isChunkError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // An intentional reload is already in progress (e.g. device switch).
      // Show a spinner instead of the error card so users don't see a
      // flash of "Något gick fel" just before the page reloads.
      let reloadPending = false;
      try { reloadPending = sessionStorage.getItem('pwa_reloading') === '1'; } catch {}
      if (reloadPending) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        );
      }

      // Chunk error with loop guard active — show "new version" message
      if (this.state.isChunkError && !shouldAutoReload()) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <RefreshCw className="h-5 w-5" />
                  Ny version tillgänglig
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Appen har uppdaterats. Ladda om sidan för att fortsätta.
                </p>
                <Button onClick={this.handleReset} className="w-full">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Ladda om sidan
                </Button>
              </CardContent>
            </Card>
          </div>
        );
      }

      // Chunk error with auto-reload pending — return null to avoid UI flash
      if (this.state.isChunkError) {
        return null;
      }

      // Generic error fallback
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Något gick fel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Ett oväntat fel uppstod när sidan skulle laddas. Försök att ladda om sidan.
              </p>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-xs font-mono text-destructive break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={this.handleReset} className="flex-1">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Ladda om sidan
                </Button>
                <Button
                  onClick={() => window.history.back()}
                  variant="outline"
                  className="flex-1"
                >
                  Tillbaka
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
